import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Clock } from "lucide-react";
import { toast } from "sonner";

const DIAS = [
    { v: 0, l: "Lun" }, { v: 1, l: "Mar" }, { v: 2, l: "Mié" },
    { v: 3, l: "Jue" }, { v: 4, l: "Vie" }, { v: 5, l: "Sáb" }, { v: 6, l: "Dom" },
];

const empty = {
    nombre: "",
    horario_inicio: "08:00",
    horario_fin: "22:00",
    dias_disponibles: [0, 1, 2, 3, 4],
    granularidad_min: 30,
    antelacion_cancelacion_min: 0,
    // defaults silenciosos para mantener compat con backend
    capacidad: 1,
    descripcion: "",
    foto_url: "",
    activa: true,
};

function SalasImpl({ embedded = false }, ref) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/salas").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
    useImperativeHandle(ref, () => ({ openNew }), []);
    const openEdit = (s) => {
        setEditing(s.sala_id);
        setForm({
            nombre: s.nombre,
            horario_inicio: s.horario_inicio,
            horario_fin: s.horario_fin,
            dias_disponibles: s.dias_disponibles || [0, 1, 2, 3, 4],
            granularidad_min: s.granularidad_min,
            antelacion_cancelacion_min: s.antelacion_cancelacion_min,
            capacidad: s.capacidad ?? 1,
            descripcion: s.descripcion ?? "",
            foto_url: s.foto_url ?? "",
            activa: s.activa ?? true,
        });
        setOpen(true);
    };

    const toggleDia = (d) => {
        const has = form.dias_disponibles.includes(d);
        setForm({
            ...form,
            dias_disponibles: has
                ? form.dias_disponibles.filter((x) => x !== d)
                : [...form.dias_disponibles, d].sort(),
        });
    };

    const submit = async () => {
        if (!form.nombre.trim()) return toast.error("Ingresá un nombre");
        try {
            if (editing) {
                await api.patch(`/salas/${editing}`, form);
                toast.success("Sala actualizada");
            } else {
                await api.post("/salas", form);
                toast.success("Sala creada");
            }
            setOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            {!embedded && (
                <PageHeader
                    title="Salas"
                    subtitle="Espacios reservables del edificio."
                    actions={<Button onClick={openNew} data-testid="sala-new"><Plus className="h-4 w-4 mr-1" /> Nueva sala</Button>}
                />
            )}

            {items.length === 0 ? (
                <EmptyState title="Aún no hay salas" description="Creá la primera sala para comenzar." />
            ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {items.map((s) => (
                        <Card key={s.sala_id} data-testid={`sala-card-${s.sala_id}`}>
                            <CardContent className="p-4">
                                <h3 className="font-heading font-bold text-base">{s.nombre}</h3>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1.5">
                                    <Clock className="h-3.5 w-3.5" />
                                    <span className="font-mono">{s.horario_inicio}–{s.horario_fin}</span>
                                </div>
                                <div className="mt-2.5 flex flex-wrap gap-1">
                                    {DIAS.map((d) => (
                                        <span key={d.v} className={`text-[10px] px-1.5 py-0.5 rounded border ${s.dias_disponibles?.includes(d.v) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>
                                            {d.l}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-3 text-[11px] text-muted-foreground">
                                    Granularidad: <span className="font-mono text-foreground">{s.granularidad_min} min</span>
                                </div>
                                <div className="mt-4">
                                    <Button variant="outline" size="sm" onClick={() => openEdit(s)} data-testid={`sala-edit-${s.sala_id}`}>
                                        <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>{editing ? "Editar sala" : "Nueva sala"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Nombre</Label>
                            <Input data-testid="sala-form-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Granularidad (min)</Label>
                            <Input type="number" min="15" step="15" value={form.granularidad_min} onChange={(e) => setForm({ ...form, granularidad_min: parseInt(e.target.value || "30") })} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Horario inicio</Label>
                                <Input type="time" value={form.horario_inicio} onChange={(e) => setForm({ ...form, horario_inicio: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Horario fin</Label>
                                <Input type="time" value={form.horario_fin} onChange={(e) => setForm({ ...form, horario_fin: e.target.value })} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Días disponibles</Label>
                            <div className="flex flex-wrap gap-1.5">
                                {DIAS.map((d) => {
                                    const has = form.dias_disponibles.includes(d.v);
                                    return (
                                        <button type="button" key={d.v} onClick={() => toggleDia(d.v)}
                                            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${has ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-primary-soft"}`}>
                                            {d.l}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} data-testid="sala-form-submit">{editing ? "Guardar" : "Crear"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const Salas = forwardRef(SalasImpl);
export default Salas;
